import React, { useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { capitalize } from '../../lib/String';
import ConversationForm from './Form';
import AttachmentEntry from './AttachmentEntry';
import { ConversationSubmitInput } from './Input';
import { ConversableType } from '../../enums/Conversations';
import { MentionProps } from '../Input/RichTextEditor';

interface Props {
  authorId?: string;
  buttonLabel?: string;
  conversableId?: string;
  conversableType?: ConversableType;
  onPublish: (input: ConversationSubmitInput) => Promise<void>;
  onTextInputClick: () => void;
  placeholder?: string;
  mentions?: MentionProps;
}

const InlineConversationInput: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const {
    authorId,
    buttonLabel,
    conversableId,
    conversableType,
    mentions,
    onPublish,
    onTextInputClick,
    placeholder = 'Type your message...',
  } = props;

  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (input: ConversationSubmitInput) => {
    await onPublish(input);
    inputRef.current?.focus();
  };

  const onMessageChange = (newValue: string) => {
    setMessage(capitalize(newValue));
  };

  return (
    <ConversationForm
      authorId={authorId}
      attachments={attachments}
      buttonLabel={buttonLabel}
      conversableId={conversableId}
      conversableType={conversableType}
      disabled={saving}
      mentions={mentions}
      message={message}
      multiline
      onSubmit={handleSubmit}
      onTextInputClick={onTextInputClick}
      placeholder={placeholder}
      saving={saving}
      setAttachments={setAttachments}
      setMessage={onMessageChange}
      setSaving={setSaving}
    >
      {({
        renderAttachmentButton,
        renderAttachments,
        renderInput,
        renderSubmitButton,
      }) => (
        <>
          {renderInput(inputRef)}
          {renderAttachments()}
          <Box pt={2} width="100%">
            <Grid
              alignItems="center"
              container
              justifyContent="flex-end"
              spacing={1}
              wrap="nowrap"
            >
              <Grid item>{renderAttachmentButton()}</Grid>
              <Grid item xs />
              <Grid item>{renderSubmitButton()}</Grid>
            </Grid>
          </Box>
        </>
      )}
    </ConversationForm>
  );
};

export default InlineConversationInput;
